import React from 'react'
import { Box, Typography } from '@mui/material'
import CloudIcon from '@mui/icons-material/Cloud'
import { getTenantTheme } from '../themes'

interface AppLogoProps {
  /** Oculta o texto do título (útil em layouts compactos) */
  hideTitle?: boolean
  /** Sobrescreve o ID do tenant (usa VITE_APP_TENNANT por padrão) */
  tenantId?: string
  /**
   * Tamanho do ícone/logo
   * - 'md' (padrão): 36-40px — header
   * - 'lg': 64px — tela de login
   */
  size?: 'md' | 'lg'
  /** Formato do container do ícone */
  shape?: 'rounded' | 'circle'
}

/**
 * Logo da aplicação customizável por tenant.
 *
 * Comportamento:
 * - logo.type === 'icon'  → renderiza ícone MUI com gradiente do tema
 * - logo.type === 'image' → renderiza <img> com o arquivo em public/tenants/{id}/logo.svg
 *
 * O tenant é resolvido na seguinte ordem:
 *   1. prop tenantId
 *   2. VITE_APP_TENNANT (env)
 *   3. tema padrão (fallback)
 */
export default function AppLogo({
  hideTitle = false,
  tenantId,
  size = 'md',
  shape = 'rounded',
}: AppLogoProps) {
  const resolvedId = tenantId ?? import.meta.env.VITE_APP_TENNANT
  const theme = getTenantTheme(resolvedId)
  const { logo, palette, appTitle, showHeaderLogo } = theme

  const isLarge = size === 'lg'
  const isCircle = shape === 'circle'
  const gradient = logo.type === 'icon' && logo.iconGradient
    ? `linear-gradient(135deg, ${logo.iconGradient[0]} 0%, ${logo.iconGradient[1]} 100%)`
    : `linear-gradient(135deg, ${palette.primary} 0%, ${palette.primaryDark ?? palette.primary} 100%)`
    
  const iconBox = (
    <Box
      sx={{
        width: isLarge ? 64 : { xs: 36, sm: 40 },
        height: isLarge ? 64 : { xs: 36, sm: 40 },
        borderRadius: isCircle ? '50%' : 2,
        background: gradient,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <CloudIcon sx={{ fontSize: isLarge ? 32 : { xs: 20, sm: 24 }, color: '#fff' }} />
    </Box>
  )

  const imageBox = logo.type === 'image' && logo.src ? (
    <Box
      component="img"
      src={logo.src}
      alt={logo.alt ?? appTitle}
      sx={{
        width: { xs: logo.width?.xs ?? 36, sm: logo.width?.sm ?? 40, md: logo.width?.md ?? 64, lg: logo.width?.lg ?? 64 },
        height: { xs: logo.height?.xs ?? 36, sm: logo.height?.sm ?? 40, md: logo.height?.md ?? 64, lg: logo.height?.lg ?? 64 },
        objectFit: 'contain',
        flexShrink: 0,
      }}
    />
  ) : null

  return (
    <>
      { !!showHeaderLogo && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: isLarge ? 'column' : 'row',
            alignItems: 'center',
            gap: isLarge ? 1.5 : { xs: 1, sm: 1.5 },
            minWidth: 0,
          }}
        >
          {logo.type === 'image' && imageBox ? imageBox : iconBox}

          {!hideTitle && (
            <Typography
              variant={isLarge ? 'h5' : 'h6'}
              sx={{
                fontWeight: 700,
                fontSize: isLarge ? '1.4rem' : { xs: '1rem', sm: '1.15rem' },
                color: '#1a1a1a',
                whiteSpace: 'nowrap',
              }}
            >
              {appTitle}
            </Typography>
          )}
        </Box>
      )}
      { !showHeaderLogo && (
        <>
        {!hideTitle && (
            <Typography
              variant={isLarge ? 'h5' : 'h6'}
              sx={{
                fontWeight: 700,
                fontSize: isLarge ? '1.4rem' : { xs: '1rem', sm: '1.15rem' },
                color: '#1a1a1a',
                whiteSpace: 'nowrap',
              }}
            >
              {appTitle}
            </Typography>
          )}
        </>
      )}
    </>
  )
}
